import { createSlice, PayloadAction, createSelector } from "@reduxjs/toolkit";
import {
  CustomFieldDTO,
  BaseCustomFieldDTO,
  GetCustomFieldDTOResponse,
  GetBaseCustomFieldDTOResponse,
  GetFieldPurposeResponse,
  FieldPurposes,
} from "../../models/CustomField.model";
import {
  getCustomField,
  getBaseCustomField,
  createOrUpdateCustomField,
  deleteCustomField,
  SetCustomFieldValue,
  updateCustomFieldPositionIdx,
  GetFieldPurpose,
} from "../thunks/customFieldThunks";
import { RootState } from "../store";
import { projectSwitched } from "../actions/projectLifecycleActions";

interface CustomFieldState {
  customFields: CustomFieldDTO[];
  baseCustomFields: BaseCustomFieldDTO[];
  fieldPurposes: FieldPurposes[];
  loading: boolean;
  operationLoading: boolean;
  error: string | null;
  success: boolean;
  message: string;
  initialized: boolean;
  baseInitialized: boolean;
  purposesInitialized: boolean;
}

const initialState: CustomFieldState = {
  customFields: [],
  baseCustomFields: [],
  fieldPurposes: [],
  loading: false,
  operationLoading: false,
  error: null,
  success: false,
  message: "",
  initialized: false,
  baseInitialized: false,
  purposesInitialized: false,
};

const customFieldSlice = createSlice({
  name: "customField",
  initialState,
  reducers: {
    clearMessages: (state) => {
      state.error = null;
      state.success = false;
      state.message = "";
    },
    resetCustomFieldState: (state) => {
      Object.assign(state, initialState);
    },
    clearCustomFieldsOnCompanyChange: (state) => {
      state.customFields = [];
      state.baseCustomFields = [];
      state.initialized = false;
      state.baseInitialized = false;
      state.error = null;
      state.success = false;
      state.message = "";
    },
    resetBaseInitialized: (state) => {
      state.baseInitialized = false;
      state.baseCustomFields = [];
    },
    // ✅ הוסף reducer חדש לניקוי ערכים
    clearCustomFieldValues: (state) => {
      state.customFields = state.customFields.map((field) => ({
        ...field,
        customFieldValue: "",
      }));
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(projectSwitched, (state) => {
        state.customFields = state.customFields.map((field) => ({
          ...field,
          customFieldValue: "",
        }));
        state.error = null;
      })
      // Get Custom Fields
      .addCase(getCustomField.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        getCustomField.fulfilled,
        (state, action: PayloadAction<GetCustomFieldDTOResponse>) => {
          state.customFields = action.payload.customFields || [];
          state.initialized = true;
          state.loading = false;
          state.error = null;
        },
      )
      .addCase(getCustomField.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "שגיאה בטעינת השדות המותאמים";
        state.initialized = false;
      })

      // Get Base Custom Fields
      .addCase(getBaseCustomField.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        getBaseCustomField.fulfilled,
        (state, action: PayloadAction<GetBaseCustomFieldDTOResponse>) => {
          state.baseCustomFields = action.payload.customFields || [];
          state.baseInitialized = true;
          state.loading = false;
          state.error = null;
        },
      )
      .addCase(getBaseCustomField.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "שגיאה בטעינת השדות המותאמים הבסיסיים";
        state.baseInitialized = false;
      })

      // Create/Update Custom Field
      .addCase(createOrUpdateCustomField.pending, (state) => {
        state.operationLoading = true;
        state.error = null;
        state.success = false;
        state.message = "";
      })
      .addCase(createOrUpdateCustomField.fulfilled, (state, action) => {
        state.operationLoading = false;
        state.success = true;
        state.message = action.payload.message || "השדה המותאם נשמר בהצלחה";
        state.error = null;

        // עדכון אופטימלי - הוסף או עדכן שדה
        const {
          projectTypeID,
          customFieldID,
          customFieldLabel,
          customFieldFieldType,
          customFieldOptions,
          customFieldIsRequired,
          purposeCode,
        } = action.meta.arg;

        if (customFieldID === 0) {
          // הוספת שדה חדש
          const newCustomField: CustomFieldDTO = {
            customFieldID: action.payload.customFieldID,
            customFieldLabel,
            customFieldValue: "",
            customFieldIsRequired,
            customFieldFieldType,
            customFieldOptions,
            customFieldValueLastUpdate: new Date(),
            customFieldUpdateBy: "",
            customFieldProjectTypeID: projectTypeID,
            customField_PurposeCode: purposeCode,
          };

          const newBaseField: BaseCustomFieldDTO = {
            customFieldID: action.payload.customFieldID,
            customFieldLabel,
            customFieldIsRequired,
            customFieldFieldType,
            customFieldOptions,
            customFieldDefaultValue: "",
            customField_IsBaseField: true,
            customField_PurposeCode: purposeCode,
          };

          // הוסף לשני המערכים
          state.customFields.push(newCustomField);
          state.baseCustomFields.push(newBaseField);
        } else {
          // עדכון שדה קיים
          const fieldIndex = state.customFields.findIndex(
            (f) => f.customFieldID === customFieldID,
          );
          if (fieldIndex !== -1) {
            state.customFields[fieldIndex] = {
              ...state.customFields[fieldIndex],
              customFieldLabel,
              customFieldFieldType,
              customFieldOptions,
              customFieldIsRequired,
              customFieldProjectTypeID: projectTypeID,
              customFieldValueLastUpdate: new Date(),
            };
          }

          const baseFieldIndex = state.baseCustomFields.findIndex(
            (f) => f.customFieldID === customFieldID,
          );
          if (baseFieldIndex !== -1) {
            state.baseCustomFields[baseFieldIndex] = {
              ...state.baseCustomFields[baseFieldIndex],
              customFieldLabel,
              customFieldFieldType,
              customFieldOptions,
              customFieldIsRequired,
              customField_PurposeCode: purposeCode,
            };
          }
        }
      })
      .addCase(createOrUpdateCustomField.rejected, (state, action) => {
        state.operationLoading = false;
        state.error = action.payload || "שגיאה בשמירת השדה המותאם";
        state.success = false;
      })

      // Set Custom Field Value
      .addCase(SetCustomFieldValue.pending, (state) => {
        state.operationLoading = true;
        state.error = null;
        state.success = false;
        state.message = "";
      })
      .addCase(SetCustomFieldValue.fulfilled, (state, action) => {
        state.operationLoading = false;
        state.success = true;
        state.message = action.payload.message || "השדה המותאם נשמר בהצלחה";
        state.error = null;

        const { customFieldID, customFieldValue } = action.meta.arg;
        const field = state.customFields.find(
          (field) => field.customFieldID === customFieldID,
        );
        if (field) {
          field.customFieldValue = customFieldValue;
          field.customFieldValueLastUpdate = new Date();
        }
      })
      .addCase(SetCustomFieldValue.rejected, (state, action) => {
        state.operationLoading = false;
        state.error = action.payload || "שגיאה בשמירת השדהדותאם";
        state.success = false;
      })

      // Delete Custom Field
      .addCase(deleteCustomField.pending, (state) => {
        state.operationLoading = true;
        state.error = null;
        state.success = false;
        state.message = "";
      })
      .addCase(deleteCustomField.fulfilled, (state, action) => {
        state.operationLoading = false;
        state.success = true;
        state.message = action.payload.message || "השדה המותאם נמחק בהצלחה";
        state.error = null;

        // הסר את השדה מהמערך המקומי
        const deletedFieldId = action.meta.arg.customFieldID;

        // מחיקה מ-customFields (לפרויקטים רגילים)
        state.customFields = state.customFields.filter(
          (field) => field.customFieldID !== deletedFieldId,
        );

        // מחיקה מ-baseCustomFields (להגדרות)
        state.baseCustomFields = state.baseCustomFields.filter(
          (field) => field.customFieldID !== deletedFieldId,
        );
      })
      .addCase(deleteCustomField.rejected, (state, action) => {
        state.operationLoading = false;
        state.error = action.payload || "שגיאה במחיקת השדה המותאם";
        state.success = false;
      })
      .addCase(updateCustomFieldPositionIdx.pending, (state) => {
        state.operationLoading = true;
        state.error = null;
      })
      .addCase(updateCustomFieldPositionIdx.fulfilled, (state, action) => {
        state.operationLoading = false;
        state.success = true;
        state.message = action.payload.message || "המיקום עודכן בהצלחה";
        state.error = null;

        // איפוס כדי לטעון מחדש את הנתונים עם המיקום החדש
        state.initialized = false;
        state.baseInitialized = false;
      })
      .addCase(updateCustomFieldPositionIdx.rejected, (state, action) => {
        state.operationLoading = false;
        state.error = action.payload || "שגיאה בעדכון המיקום";
        state.success = false;
      })

      // Get Field Purposes
      .addCase(GetFieldPurpose.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        GetFieldPurpose.fulfilled,
        (state, action: PayloadAction<GetFieldPurposeResponse>) => {
          state.fieldPurposes = action.payload.fieldPurposes || [];
          state.purposesInitialized = true;
          state.loading = false;
          state.error = null;
        },
      )
      .addCase(GetFieldPurpose.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "שגיאה בטעינת מטרות השדות";
        state.purposesInitialized = false;
      });
  },
});

// Selectors
const selectCustomFieldState = (state: RootState) => state.customField;

export const selectCustomFields = (state: RootState) =>
  state.customField.customFields;
export const selectBaseCustomFields = (state: RootState) =>
  state.customField.baseCustomFields;
export const selectCustomFieldLoading = (state: RootState) =>
  state.customField.loading;
export const selectCustomFieldError = (state: RootState) =>
  state.customField.error;
export const selectCustomFieldInitialized = (state: RootState) =>
  state.customField.initialized;
export const selectBaseCustomFieldInitialized = (state: RootState) =>
  state.customField.baseInitialized;
export const selectFieldPurposes = (state: RootState) =>
  state.customField.fieldPurposes;
export const selectFieldPurposesInitialized = (state: RootState) =>
  state.customField.purposesInitialized;

// סלקטור לשדות כלליים (customFieldProjectTypeID = 0)
export const selectGeneralCustomFields = createSelector(
  [selectCustomFieldState],
  (customFieldState) =>
    customFieldState.customFields.filter(
      (field) => field.customFieldProjectTypeID === 0,
    ),
);

// סלקטור לשדות לפי סוג פרויקט
export const selectCustomFieldsByProjectType = createSelector(
  [
    selectCustomFieldState,
    (_: RootState, projectTypeId: number) => projectTypeId,
  ],
  (customFieldState, projectTypeId) =>
    customFieldState.customFields.filter(
      (field) => field.customFieldProjectTypeID === projectTypeId,
    ),
);

// סלקטור לשדות פרווקט (כלליים + ספציפיים לסוג פרויקט)
export const selectProjectCustomFields = createSelector(
  [
    selectCustomFieldState,
    (_: RootState, projectTypeId: number) => projectTypeId,
  ],
  (customFieldState, projectTypeId) =>
    customFieldState.customFields.filter(
      (field) =>
        field.customFieldProjectTypeID === 0 ||
        field.customFieldProjectTypeID === projectTypeId,
    ),
);

export const {
  clearMessages,
  resetCustomFieldState,
  clearCustomFieldsOnCompanyChange,
  clearCustomFieldValues,
  resetBaseInitialized,
} = customFieldSlice.actions;

export default customFieldSlice.reducer;
